import {
  ContributorFeedback,
  ContributorFeedbackItem,
  FeedbackPriority,
  InteractionStepResponse,
  FeedbackType,
  ContributorFeedbackTextInlinePosition,
  getMarkdownContent,
  STEPS_WITH_MARKDOWN_CONTENT,
} from '@scale/shared/types/chat';
import uuid from 'uuid';
import { VFileMessage } from 'vfile-message';
import { logEvent } from 'react-scale/lib/events';
import { Event } from '@scale/shared/types/events/index';
import {
  MarkdownValidationFlaggedStep,
  RemarkLintTransformedMessage,
} from '@scale/shared/types/events/MarkdownValidationIssueEvent';
import _ from 'lodash';

type LogValidationMessagesParams = {
  assignmentId?: string;
  taskId?: string;
  projectId?: string;
  workerId?: string;
  flaggedStep: MarkdownValidationFlaggedStep;
};

const logValidationMessage = async ({
  assignmentId,
  projectId,
  workerId,
  flaggedStep,
}: LogValidationMessagesParams): Promise<void> => {
  const transformedEvent = {
    assignmentId,
    projectId,
    workerId,
    originalContent: flaggedStep.originalContent,
    preprocessedOriginalContent: flaggedStep.preprocessedOriginalContent,
    fixedContent: flaggedStep.fixedContent,
    responseIndex: flaggedStep.responseIndex,
    stepType: flaggedStep.stepType,
    stepId: flaggedStep.stepId,
    validationIssueMessages: flaggedStep.validationIssueMessages,
  };

  // Log to Snowflake
  await logEvent(Event.MarkdownValidationIssueEvent, transformedEvent);
};

/**
 * Calculates the global start offset of a given line in a piece of text.
 * @param text - The complete text string.
 * @param lineNumber - The line number (1-based index) for which to calculate the global start offset.
 * @returns The global start offset for the specified line, or -1 if the line number is invalid.
 */
export function getGlobalStartOffset(text: string, lineNumber: number): number | undefined {
  // Ensure the line number is valid
  if (lineNumber < 1) {
    throw new Error('Line number must be a positive integer.');
  }

  let currentOffset = 0;
  let currentLine = 1;

  for (let i = 0; i < text.length; i++) {
    if (currentLine === lineNumber) {
      return currentOffset;
    }

    // Check for line breaks (\n or \r\n)
    if (text[i] === '\n') {
      currentLine++;
      currentOffset = i + 1; // Offset is one character after the newline
    }
  }

  // If we reach here, the line number is out of range
  if (currentLine < lineNumber) {
    return undefined; // Invalid line number
  }

  return currentOffset;
}

/**
 * Calculates the global end offset of a given line in a piece of text.
 * @param text - The complete text string.
 * @param lineNumber - The line number (1-based index) for which to calculate the global end offset.
 * @returns The global end offset for the specified line, or -1 if the line number is invalid.
 */
export function getGlobalEndOffset(text: string, lineNumber: number): number | undefined {
  // Ensure the line number is valid
  if (lineNumber < 1) {
    throw new Error('Line number must be a positive integer.');
  }

  let currentLine = 1;

  for (let i = 0; i < text.length; i++) {
    // Check for line breaks (\n or \r\n)
    if (text[i] === '\n') {
      if (currentLine === lineNumber) {
        return i - 1; // End offset is one character before the newline
      }
      currentLine++;
    }
  }

  // Handle the case where the last line doesn't end with a newline
  if (currentLine === lineNumber) {
    return text.length - 1; // The last character in the text
  }

  return undefined;
}

/**
 * Calculates the inline position of a given message in a piece of content.
 * @param message - The message to calculate the inline position for.
 * @param content - The content to calculate the inline position for.
 * @returns The inline position of the message in the content.
 */
const getMessageInlinePosition = (
  message: VFileMessage,
  content: string,
): ContributorFeedbackTextInlinePosition | undefined => {
  if (!message.position) {
    return undefined;
  }
  let startOffset = message.position.start.offset;
  let endOffset = message.position.end.offset;

  if (!startOffset && !endOffset) {
    return undefined;
  }

  // If the start offset and end offset are on the same line, return the start and end offsets without modification
  const startOffsetLine = message.position.start.line;
  const endOffsetLine = message.position.end.line;
  if (startOffsetLine === endOffsetLine && !_.isNil(startOffset) && !_.isNil(endOffset)) {
    return {
      type: 'Text',
      start: startOffset,
      end: endOffset,
      quotedText: content.slice(startOffset, endOffset),
    };
  }

  // If the start offset and end offset are on different lines, always extend the offsets to the start and end of the
  // first and last lines respectively. Feedback on multiple lines should not omit the start of the first line or the
  // end of the last line, since this missing context can cause confusion for the contributor.
  if (!_.isNil(startOffsetLine)) {
    startOffset = getGlobalStartOffset(content, startOffsetLine);
  }

  if (!_.isNil(endOffsetLine)) {
    endOffset = getGlobalEndOffset(content, endOffsetLine);
  }

  return {
    type: 'Text',
    start: startOffset,
    end: endOffset,
    quotedText: content.slice(startOffset, endOffset),
  };
};

const trimTrailingNewline = (content: string): string => {
  return content.endsWith('\n') ? content.slice(0, -1) : content;
};

const transformVFileMessages = (messages: VFileMessage[]): RemarkLintTransformedMessage[] => {
  return messages.map(message => ({
    name: message.name,
    message: message.message,
    fatal: message.fatal ?? undefined,
    position: {
      start: message.position?.start.offset ?? undefined,
      end: message.position?.end.offset ?? undefined,
    },
    reason: message.reason ?? undefined,
    stack: message.stack ?? undefined,
    line: message.line ?? undefined,
    column: message.column ?? undefined,
    source: message.source ?? undefined,
    ruleId: message.ruleId ?? undefined,
    expected: message.expected ?? undefined,
    actual: message.actual ?? undefined,
    url: message.url ?? undefined,
    note: message.note ?? undefined,
  }));
};

export const transformRemarkValidationMessagesToUnifiedFeedback = ({
  originalContent,
  messages,
}: {
  originalContent: string;
  messages: VFileMessage[];
}): ContributorFeedbackItem[] => {
  const priority = FeedbackPriority.HIGH;

  return messages.map(message => {
    const title = 'Markdown validation warning';
    const content = message.message;
    return {
      id: uuid.v4(),
      type: FeedbackType.MarkdownValidation,
      title,
      content,
      priority,
      inlinePosition: getMessageInlinePosition(message, originalContent),

      // TODO: Note that markdown validation feedback is not blocking for now by default.
      // We should make it blocking in the future based on `!!message.fatal`, which is configurable
      // for each `remarkLint` rule.
      isBlocking: false,
    };
  });
};

export type ValidationOptions = {
  isMarkdownValidationLoggingEnabled: boolean;
  shouldOmitFeedback: boolean;
};

export type ValidateStepMarkdownParams = {
  taskId?: string;
  assignmentId?: string;
  projectId?: string;
  workerId?: string;
  stepResponse: InteractionStepResponse;
  stepId?: string;
  responseIndex: number;
  validationOptions: ValidationOptions;
  markdownValidationFn: (
    content: string,
    shouldProcessNonMathTokens?: boolean,
  ) => Promise<{
    fixedContent: string;
    messages: VFileMessage[];
    preprocessedOriginalContent: string;
  }>;
};

export const validateStepMarkdown = async ({
  assignmentId,
  projectId,
  workerId,
  taskId,
  stepResponse,
  stepId,
  responseIndex,
  validationOptions,
  markdownValidationFn,
}: ValidateStepMarkdownParams): Promise<ContributorFeedback> => {
  if (!STEPS_WITH_MARKDOWN_CONTENT.includes(stepResponse.type)) {
    return [];
  }

  const originalContent = getMarkdownContent(stepResponse);

  if (!originalContent) {
    return [];
  }

  const { shouldOmitFeedback, isMarkdownValidationLoggingEnabled } = validationOptions;

  const { messages, fixedContent, preprocessedOriginalContent } = await markdownValidationFn(
    originalContent,

    /**
     * We should only process non-math tokens if we are logging validation issues and omitting displaying feedback to CBs.
     * Otherwise, any messages we display to CBs may contain standard tokens that replaced custom tokens, which will cause
     * confusion for the contributor.
     */
    isMarkdownValidationLoggingEnabled && shouldOmitFeedback,
  );

  // Remark adds an extra newline at the end of `fixedContent`, so we need to trim it.
  const trimmedFixedContent = trimTrailingNewline(fixedContent);

  /**
   * A diff between `preprocessedOriginalContent` and `fixedContent` means that the markdown validation
   * has detected an issue and fixed it. We should log this as a validation issue.
   *
   * Note that we use `preprocessedOriginalContent` instead of `originalContent` for this comparison
   * because remark processing does not support custom math token delimeters, meaning `fixedContent` will
   * always use standard math token delimeters. `preprocessedOriginalContent` is the `originalContent`
   * but with custom math tokens delimeters replaced with standard delimeters to ensure the comparison
   * is consistent.
   */
  if (isMarkdownValidationLoggingEnabled && preprocessedOriginalContent !== trimmedFixedContent) {
    const validationIssueMessages = transformVFileMessages(messages);

    await logValidationMessage({
      assignmentId,
      projectId,
      workerId,
      taskId,
      flaggedStep: {
        originalContent,
        preprocessedOriginalContent,
        fixedContent: trimmedFixedContent,
        responseIndex,
        stepType: stepResponse.type,
        stepId,
        validationIssueMessages,
      },
    });
  }

  if (shouldOmitFeedback) {
    return [];
  }

  return transformRemarkValidationMessagesToUnifiedFeedback({
    originalContent,
    messages,
  });
};
